import { useRouteError, isRouteErrorResponse, useNavigate } from "react-router-dom";
import {
    Box,
    Typography,
    Button,
    Paper,
    Stack,
    Divider,
    Snackbar,
    IconButton,
    Tooltip,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HomeIcon from "@mui/icons-material/Home";
import RefreshIcon from "@mui/icons-material/Refresh";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseIcon from "@mui/icons-material/Close";
import { useState } from "react";

/**
 * ErrorPage
 *
 * Pantalla de error personalizada para React Router.
 * - Muestra errores de rutas (RouteErrorResponse) o excepciones JS.
 * - Permite copiar el detalle del error al portapapeles.
 * - Ofrece botones de navegación y recarga.
 */
export default function ErrorPage() {
    const error = useRouteError();
    const navigate = useNavigate();
    const [openCopySnackbar, setOpenCopySnackbar] = useState(false);

    console.error("🔴 Error atrapado por ErrorPage:", error);

    const handleGoHome = () => navigate("/dashboard/home");
    const handleReload = () => window.location.reload();

    const handleCopyError = async () => {
        let textToCopy = "";

        if (isRouteErrorResponse(error)) {
            textToCopy = `${error.status} ${error.statusText}\n${error.data?.message ?? ""}`;
        } else if (error instanceof Error) {
            textToCopy = `${error.name}: ${error.message}\n\n${error.stack}`;
        } else {
            textToCopy = JSON.stringify(error, null, 2);
        }

        try {
            await navigator.clipboard.writeText(textToCopy);
            setOpenCopySnackbar(true);
        } catch (err) {
            console.warn("No se pudo copiar el error:", err);
        }
    };

    let title = "😔 Error inesperado 😔";
    let description = "Ha ocurrido un problema inesperado.";
    let details: string | undefined;

    if (isRouteErrorResponse(error)) {
        title = `${error.status} - ${error.statusText}`;
        description =
            error.data?.message ||
            "Ocurrió un error al cargar los datos o procesar la acción.";
    } else if (error instanceof Error) {
        description = error.message;
        details = error.stack;
    }

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "background.default",
                p: 3,
            }}
        >
            <Paper
                elevation={5}
                sx={{
                    p: 5,
                    maxWidth: 640,
                    textAlign: "center",
                    borderRadius: 3,
                }}
            >
                <Stack spacing={3} alignItems="center">
                    <ErrorOutlineIcon sx={{ fontSize: 70, color: "error.main" }} />

                    <Typography variant="h5" fontWeight={600}>
                        {title}
                    </Typography>

                    <Typography variant="body1" color="text.secondary">
                        {description}
                    </Typography>

                    {details && (
                        <>
                            <Divider sx={{ width: "100%", my: 2 }} />
                            <Box
                                sx={{
                                    position: "relative",
                                    width: "100%",
                                }}
                            >
                                <Typography
                                    component="pre"
                                    sx={{
                                        textAlign: "left",
                                        bgcolor: "#f9f9f9",
                                        p: 2,
                                        borderRadius: 1,
                                        fontSize: "0.8rem",
                                        overflowX: "auto",
                                        color: "text.secondary",
                                    }}
                                >
                                    {details}
                                </Typography>

                                <Tooltip title="Copiar error al portapapeles">
                                    <IconButton
                                        onClick={handleCopyError}
                                        size="small"
                                        sx={{
                                            position: "absolute",
                                            top: 8,
                                            right: 8,
                                            bgcolor: "white",
                                            "&:hover": { bgcolor: "grey.100" },
                                        }}
                                    >
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </>
                    )}

                    <Stack direction="row" spacing={2}>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<HomeIcon />}
                            onClick={handleGoHome}
                        >
                            Ir al inicio
                        </Button>

                        <Button
                            variant="outlined"
                            color="secondary"
                            startIcon={<RefreshIcon />}
                            onClick={handleReload}
                        >
                            Recargar
                        </Button>
                    </Stack>
                </Stack>
            </Paper>

            {/* Snackbar de confirmación */}
            <Snackbar
                open={openCopySnackbar}
                autoHideDuration={3000}
                onClose={() => setOpenCopySnackbar(false)}
                message="Error copiado al portapapeles"
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                action={
                    <IconButton
                        size="small"
                        color="inherit"
                        onClick={() => setOpenCopySnackbar(false)}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                }
            />
        </Box>
    );
}
